import { buildParentMap, ChildrenMap } from '@/components/shared/Standards/standardsTreeUtils';
import { GradeGroup, StandardNode } from './types';

export { buildChildrenMap, buildParentMap } from '@/components/shared/Standards/standardsTreeUtils';
export type { ChildrenMap } from '@/components/shared/Standards/standardsTreeUtils';

/* eslint-disable sort-keys */
const GRADE_LABELS: Record<string, string> = {
  1: 'Grade 1',
  2: 'Grade 2',
  3: 'Grade 3',
  4: 'Grade 4',
  5: 'Grade 5',
  6: 'Grade 6',
  7: 'Grade 7',
  8: 'Grade 8',
  9: 'Grade 9',
  10: 'Grade 10',
  11: 'Grade 11',
  12: 'Grade 12',
  K: 'Kindergarten',
};
/* eslint-enable sort-keys */

const GRADE_ORDER = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export function countDescendants(
  nodeId: number,
  childrenMap: ChildrenMap,
): { taggable: number; total: number } {
  const children = childrenMap.get(nodeId) ?? [];
  let taggable = 0;
  let total = 0;

  children.forEach((child) => {
    total += 1;
    if (!child.has_children) {
      taggable += 1;
    }
    const sub = countDescendants(child.id, childrenMap);
    taggable += sub.taggable;
    total += sub.total;
  });

  return { taggable, total };
}

// AIDEV-NOTE: Rolled-up correlation total per node = its own direct count + all descendants'.
// A leaf's entry equals its own direct count (base case), so the pill value is always
// map.get(id); has_children only drives styling (direct vs rolled-up), not the number.
export function buildCorrelationCountMap(childrenMap: ChildrenMap): Map<number, number> {
  const map = new Map<number, number>();

  const visit = (node: StandardNode): number => {
    const children = childrenMap.get(node.id) ?? [];
    let total = node.correlation_count ?? 0;
    children.forEach((child) => { total += visit(child); });
    map.set(node.id, total);
    return total;
  };

  (childrenMap.get(null) ?? []).forEach(visit);

  return map;
}

export function groupByGrade(nodes: StandardNode[], childrenMap: ChildrenMap): GradeGroup[] {
  const roots = childrenMap.get(null) ?? [];
  const gradeMap = new Map<string, StandardNode[]>();

  roots.forEach((root) => {
    // AIDEV-NOTE: Some EdGate root nodes have null grade. Default to 'K' so they
    // appear in a visible group rather than being silently dropped.
    const grade = root.grade ?? 'K';
    const existing = gradeMap.get(grade);

    if (existing) {
      existing.push(root);
    } else {
      gradeMap.set(grade, [root]);
    }
  });

  return GRADE_ORDER
    .filter(grade => gradeMap.has(grade))
    .map((grade) => {
      const gradeRoots = gradeMap.get(grade)!;
      let taggableCount = 0;
      let totalCount = 0;

      gradeRoots.forEach((root) => {
        totalCount += 1;
        if (!root.has_children) taggableCount += 1;
        const desc = countDescendants(root.id, childrenMap);
        taggableCount += desc.taggable;
        totalCount += desc.total;
      });

      return {
        grade,
        label: GRADE_LABELS[grade] ?? `Grade ${grade}`,
        roots: gradeRoots,
        taggableCount,
        totalCount,
      };
    });
}

export function getAllExpandableIds(childrenMap: ChildrenMap): number[] {
  const ids: number[] = [];

  childrenMap.forEach((children, key) => {
    if (key !== null) ids.push(key);
  });

  return ids;
}

export function getAllGradeKeys(groups: GradeGroup[]): string[] {
  return groups.map(g => g.grade);
}

function nodeMatchesQuery(node: StandardNode, lowerQuery: string): boolean {
  if (node.alias?.toLowerCase().includes(lowerQuery)) return true;
  if (node.description_en?.toLowerCase().includes(lowerQuery)) return true;
  if (String(node.id).includes(lowerQuery)) return true;
  return false;
}

export interface SearchResult {
  matchIds: Set<number>;
  visibleIds: Set<number>;
}

export function getFilteredNodeIds(
  nodes: StandardNode[],
  query: string,
): SearchResult {
  if (!query.trim()) return { matchIds: new Set(), visibleIds: new Set() };

  const lowerQuery = query.toLowerCase().trim();
  const parentMap = buildParentMap(nodes);
  const matchIds = new Set<number>();
  const visibleIds = new Set<number>();

  nodes.forEach((node) => {
    if (nodeMatchesQuery(node, lowerQuery)) matchIds.add(node.id);
  });

  matchIds.forEach((id) => {
    visibleIds.add(id);
    let current = parentMap.get(id) ?? null;
    while (current !== null) {
      visibleIds.add(current);
      current = parentMap.get(current) ?? null;
    }
  });

  return { matchIds, visibleIds };
}

export function countMatchesInSubtree(
  rootId: number,
  childrenMap: ChildrenMap,
  matchIds: Set<number>,
): number {
  let count = matchIds.has(rootId) ? 1 : 0;
  (childrenMap.get(rootId) ?? []).forEach((child) => {
    count += countMatchesInSubtree(child.id, childrenMap, matchIds);
  });
  return count;
}
